/**
 * Moving, pinching and twisting something laid on a snap.
 *
 * One finger carries it. Two pinch it larger or smaller and twist it round,
 * and carry it along by their middle at the same time -- which is what makes
 * it feel attached to the fingers rather than to a set of sliders. Neither
 * finger has to be on it: on a phone a caption is often narrower than two
 * fingers, and a pinch that had to land both on the words could only ever
 * make them smaller.
 *
 * A grip is taken each time the number of fingers changes, so a finger added
 * or lifted mid-gesture never makes the thing jump.
 */
import {
  CAPTION_MAX_WIDTH,
  CAPTION_MIN_WIDTH,
  DECOR_MAX_SCALE,
  PHOTO_REACH,
  foldTurn,
  type Reach,
} from '../../shared/snapDecor';
import type { CaptionAt } from '../../shared/types';

export interface Point {
  x: number;
  y: number;
}

export interface Grip {
  /** The placement when this grip was taken. */
  from: CaptionAt;
  /** The fingers' middle, distance apart and angle when it was taken. */
  midX: number;
  midY: number;
  span: number;
  turn: number;
}

/** Still big enough to read or see; the server allows smaller, the eye does not. */
export const GESTURE_MIN_SCALE = 0.4;
/** Less than this far is a tap, not a drag. */
export const TAP_SLOP_PX = 6;

const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));

function middle(points: Point[]): Point {
  const [a, b] = points;
  return b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } : a;
}

function spanOf(points: Point[]): number {
  const [a, b] = points;
  return b ? Math.hypot(b.x - a.x, b.y - a.y) : 0;
}

function turnOf(points: Point[]): number {
  const [a, b] = points;
  return b ? (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI : 0;
}

export function gripOf(points: Point[], from: CaptionAt): Grip {
  const mid = middle(points);
  return { from, midX: mid.x, midY: mid.y, span: spanOf(points), turn: turnOf(points) };
}

/**
 * Where it is now, for fingers at `points` on a picture of `box`'s size, kept
 * within `reach` of it: the picture alone, or the frame round it too.
 */
export function placeAt(
  grip: Grip,
  points: Point[],
  box: { width: number; height: number },
  reach: Reach = PHOTO_REACH,
): CaptionAt {
  const mid = middle(points);
  const next: CaptionAt = {
    x: clamp(grip.from.x + (mid.x - grip.midX) / box.width, reach.x0, reach.x1),
    y: clamp(grip.from.y + (mid.y - grip.midY) / box.height, reach.y0, reach.y1),
    rotate: grip.from.rotate,
    scale: grip.from.scale,
    // Carried, so moving or pinching words never changes how they wrap.
    ...(grip.from.width !== undefined ? { width: grip.from.width } : {}),
  };
  if (points.length > 1 && grip.span > 0) {
    next.scale = clamp((grip.from.scale * spanOf(points)) / grip.span, GESTURE_MIN_SCALE, DECOR_MAX_SCALE);
    next.rotate = foldTurn(grip.from.rotate + (turnOf(points) - grip.turn));
  }
  return next;
}

/**
 * A caption's box with one edge dragged: wider or narrower, its words
 * rewrapping to fit, and the opposite edge staying where it was, so it grows
 * towards the finger. Carter: "If I grab an edge then it should expand that
 * way whether its taller or narrower."
 *
 * The finger's movement counts only along the caption's own width, so a
 * turned caption stretches along its own line rather than the screen's.
 * `width` is where the drag started, as a fraction of the frame; `box` is the
 * frame on screen, the same pixels the finger moves in.
 */
export function stretchAt(
  from: CaptionAt,
  width: number,
  edge: 'left' | 'right',
  moved: Point,
  box: { width: number; height: number },
): CaptionAt {
  const turn = (from.rotate * Math.PI) / 180;
  const cos = Math.cos(turn);
  const sin = Math.sin(turn);
  // How far the finger went along the caption's line, in screen pixels.
  const along = moved.x * cos + moved.y * sin;
  const outward = edge === 'right' ? along : -along;
  const drawn = box.width * from.scale;
  const next = clamp(width + outward / drawn, CAPTION_MIN_WIDTH, CAPTION_MAX_WIDTH);
  // Half the change moves the middle, towards the dragged edge.
  const shift = (((next - width) * drawn) / 2) * (edge === 'right' ? 1 : -1);
  return {
    ...from,
    x: from.x + (shift * cos) / box.width,
    y: from.y + (shift * sin) / box.height,
    width: next,
  };
}

/** How far a finger has to travel sideways, off everything, to change the look. */
export const SWIPE_PX = 50;

/**
 * A sideways swipe across the picture: -1 back, 1 on, or 0 for anything that
 * was not one -- too short, or more up and down than across.
 */
export function swipeOf(grip: Grip, end: Point): -1 | 0 | 1 {
  const dx = end.x - grip.midX;
  const dy = end.y - grip.midY;
  if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(dy) * 1.5) return 0;
  return dx < 0 ? 1 : -1;
}

/** Whether the fingers have gone far enough, or pinched at all, to not be a tap. */
export function hasMoved(grip: Grip, points: Point[]): boolean {
  const mid = middle(points);
  return Math.hypot(mid.x - grip.midX, mid.y - grip.midY) > TAP_SLOP_PX || points.length > 1;
}
